/** @format */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {LinearProgress} from '@mui/material';
import Alert from '@mui/material/Alert';
import Download from '@mui/icons-material/Download';

import {Trans, useTranslation} from 'react-i18next';

import {useNavigate, useSearchParams} from 'react-router-dom';

import {format, endOfDay} from 'date-fns';

import WideLayout from 'layouts/WideLayout';
import {ROUTES} from 'constants/ROUTES';

import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useCompanyContext} from 'contexts/CompanyContext';

import {Period} from 'components/PeriodSelector/model';

import {PeriodSelector} from 'components/PeriodSelector';
import {usePeriodSelector} from 'components/PeriodSelector/hooks/usePeriodSelector';
import {TableVirtualized} from 'components/Table';
import {TanStackTableData} from 'components/Table/core/types';

import {PERIODS, filterOrder} from './constants';

import {formatColumns} from './helpers/formatColumns';
import {exportTableToExcel} from './helpers/exportToExcel';
import {useYaDirectData} from './hooks/useYaDirectData';
import {useDynamicTableHeight} from './hooks/useDynamicTableHeight';
import YaDirectBreadcrumbs from './components/YaDirectBreadcrumbs';
import {
  BreadcrumbsContainer,
  DownloadExcelButton,
  PeriodSelectorContainer,
  GoBackButton,
  GoBackIcon,
} from './style';
import {useDateFromUrlParams} from './hooks/useDateUrlParams';

const YaDirectChangeLog = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();

  const filterBarRef = useRef<HTMLDivElement>(null);
  const periodSelectorRef = useRef<HTMLDivElement>(null);
  const exportDataRef = useRef<TanStackTableData[]>([]);
  const [exportDataLength, setExportDataLength] = useState(0);

  const {
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    period: defaultPeriod,
  } = useDateFromUrlParams();

  const periodProps = usePeriodSelector({
    defaultStartDate,
    defaultEndDate,
    defaultPeriod,
  });

  const {startDate, endDate, activePeriod, onChangePeriod, onChangeStartDate, onChangeEndDate} =
    periodProps;

  const {currentUserData, tableData, loading, error} = useYaDirectData({
    startDate,
    endDate,
  });

  const dynamicTableHeight = useDynamicTableHeight({
    filterBarRef,
    periodSelectorRef,
  });

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('startDate', format(startDate, 'yyyy-MM-dd'));
    newParams.set('endDate', format(endDate, 'yyyy-MM-dd'));
    setSearchParams(newParams);
  }, [startDate, endDate, searchParams, setSearchParams]);

  const handleDateRangeConfirm = (newStartDate: Date, newEndDate: Date) => {
    onChangeStartDate(newStartDate);
    onChangeEndDate(endOfDay(newEndDate));
  };

  const handleGoBackButton = () => {
    navigate(`/${ROUTES.HOME}`);
  };

  const handleChangePeriod = (newPeriod: Period) => {
    mixpanel.track('change_changelog_period', {
      project_uuid: currentCompany?.uuid,
      period: newPeriod,
    });
    onChangePeriod(newPeriod);
  };

  const handleFilterChange = (_columnId: string, _value: string | null | undefined) => {
    mixpanel.track('filter_changelog_results', {project_uuid: currentCompany?.uuid});
  };

  const columns = useMemo(() => formatColumns(t), [t]);

  const handleDownloadExcel = useCallback(async () => {
    mixpanel.track('download_changelog_excel', {project_uuid: currentCompany?.uuid});

    try {
      await exportTableToExcel({
        data: exportDataRef.current,
        columns,
        companyName: currentUserData?.currentUser?.currentCompany?.name || '',
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        excelColumnLabels: {
          valueBefore: t('Значение до'),
          valueAfter: t('Значение после'),
        },
      });
    } catch (error) {
      console.error('Excel export failed:', error);
    }
  }, [mixpanel, currentCompany?.uuid, columns, currentUserData, startDate, endDate, t]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading) {
    return (
      <WideLayout>
        <LinearProgress style={{flex: 1, marginTop: 16}} />
      </WideLayout>
    );
  }

  return (
    <WideLayout>
      <BreadcrumbsContainer>
        <YaDirectBreadcrumbs companyName={currentUserData?.currentUser?.currentCompany?.name} />
      </BreadcrumbsContainer>

      <PeriodSelectorContainer ref={periodSelectorRef}>
        <PeriodSelector
          startDate={startDate}
          endDate={endDate}
          activePeriod={activePeriod}
          onChangePeriod={handleChangePeriod}
          periodsModel={PERIODS}
          onDateRangeConfirm={handleDateRangeConfirm}
          allowSameDayRange
        />
        <GoBackButton onClick={handleGoBackButton}>
          <GoBackIcon fontSize='small' htmlColor='#fff' />
          <Trans>Назад</Trans>
        </GoBackButton>
      </PeriodSelectorContainer>

      <TableVirtualized
        columns={columns}
        data={tableData}
        defaultSortBy='time'
        defaultOrderBy='desc'
        enableFilters
        filterOrder={filterOrder}
        tableHeight={dynamicTableHeight}
        filterBarRef={filterBarRef}
        onFilterChange={handleFilterChange}
        exportDataRef={exportDataRef}
        onExportDataChange={setExportDataLength}
        filterBarActions={
          <DownloadExcelButton
            variant='outlined'
            size='small'
            onClick={handleDownloadExcel}
            disabled={exportDataLength === 0}
            startIcon={<Download />}
          >
            <Trans>Скачать Excel</Trans>
          </DownloadExcelButton>
        }
      />
    </WideLayout>
  );
};

export default YaDirectChangeLog;
